import type { CostRecipe } from "./cost-recipe";
import { directDerivation, type QuantityDerivation } from "./quantity-dimensions";
import { ensureRecipeReferenceDimensions } from "./recipe-reference-dimensions";
import { isSteelMemberWork, scopeWorkDimensions, matchesDimensionLabel } from "./work-dimensions";

const linearUnit = (unit: string) => /^(m|lm|linear_m|metres?|meters?)$/i.test(unit.trim());
const lengthLabel = (label: string) => /\b(length|span)$/i.test(label.trim()) && !/\bopening\b/i.test(label);

/** Visible, editable estimates only; never upgrade an assumption to measured evidence. */
export function ensureLengthDimensions(data: QuantityDerivation | null | undefined, unit: string,
  quantity: number, context = "", recipe?: CostRecipe | null,
  options?: { normaliseSteelAssumptions?: boolean; itemName?: string }): QuantityDerivation | undefined {
  data = scopeWorkDimensions(data, context, recipe, options?.itemName);
  data = ensureRecipeReferenceDimensions(data, recipe);
  // A priced area/volume allowance is still an editable estimating basis.
  // Do not fabricate a rectangle or cuboid when no geometric operands survived.
  const allowanceUnit = /^(?:m[²2]|sqm|sq\.?\s*m)$/i.test(unit.trim()) ? "m²"
    : /^(?:m[³3]|cum)$/i.test(unit.trim()) ? "m³" : undefined;
  if (allowanceUnit && Number.isFinite(quantity) && quantity > 0 && !data?.dimensions.length
    && (!data || data.quantity_op === "direct")) {
    const manual = data?.quantity_overridden === true;
    const label = allowanceUnit === "m²" ? "Area" : "Volume";
    const id = `allowance-${label.toLowerCase()}`;
    data = { ...(data ?? directDerivation()), dimensions: [{
      id, label, value: quantity, unit: allowanceUnit, source: manual ? "entered" : "assumed",
      ...(manual ? { entryOrigin: "manual" as const } : {}),
      note: manual ? `${label} taken from the manually entered line quantity. No separate geometric dimensions were retained.`
        : `${label} allowance taken from this line's ${quantity} ${allowanceUnit} quantity, not measured from drawings. No geometric dimensions were retained; confirm or edit this allowance.`,
    }], quantity_op: "quantity", quantity_operands: [id] };
  }
  const steel = isSteelMemberWork(data, context, recipe, options?.itemName);
  const models = [
    ...(recipe?.shape === "specification" && recipe.supply_pricing ? [recipe.supply_pricing] : []),
    ...(recipe?.materials.flatMap(m => m.dimension_pricing ? [m.dimension_pricing] : []) ?? []),
  ];
  const labels = [...new Set(models.flatMap(m => m.measure === "length" ? m.dimension_labels
    : m.measure === "weight" ? [m.length_label ?? m.dimension_labels.find(lengthLabel) ?? "Length"] : []))];
  if (steel && !labels.length) labels.push("Beam length");
  if (linearUnit(unit) && !labels.length) labels.push("Length");
  if (!labels.length) return data ?? undefined;
  const next = { ...(data ?? directDerivation()), dimensions: [...(data?.dimensions ?? [])] };
  // Generation-only correction. Never silently rewrite historical assumptions
  // when rendering, or override explicit measurements and builder entries.
  const beam = steel && (/\b(beam|rsj)\b/i.test(context) || !/\b(column|post)\b/i.test(context));
  if (options?.normaliseSteelAssumptions && beam && !linearUnit(unit)) {
    const supportedOpenings = next.dimensions.filter(d => d.unit === "m"
      && /\bopening\b.*\b(width|span)$/i.test(d.label) && d.value > 0
      && (d.source === "measured" || d.source === "entered"));
    next.dimensions = next.dimensions.map(d => {
      if (d.unit !== "m" || !lengthLabel(d.label) || d.source !== "assumed" || d.value >= 3) return d;
      // A clear opening can support a shorter derived cut length, but cannot
      // justify a member shorter than the opening itself.
      const opening = supportedOpenings.length === 1 ? supportedOpenings[0] : undefined;
      const value = opening ? Math.max(d.value, opening.value + 0.3) : 3;
      return { ...d, value: Math.round(value * 10000) / 10000,
        note: (opening
          ? `Assumed cut length from this item's ${opening.label.toLowerCase()} (${opening.value} m) plus bearing allowance; confirm actual length.`
          : `No item-specific measured opening or cut length supports the former ${d.value} m estimate. Provisional beam length 3 m; confirm against the drawings. ${d.note ?? ""}`).slice(0, 1000) };
    });
  }
  for (const label of labels) {
    if (next.dimensions.some(d => d.unit === "m" && (matchesDimensionLabel(d.label, label)
      || labels.length === 1 && lengthLabel(d.label)))) continue;
    const openings = next.dimensions.filter(d => d.unit === "m" && /\bopening\b.*\b(width|span)$/i.test(d.label));
    const opening = openings.length === 1 && openings[0].value > 0
      && (!beam || ["measured", "entered"].includes(openings[0].source)) ? openings[0] : undefined;
    const fromQuantity = linearUnit(unit) && Number.isFinite(quantity) && quantity > 0;
    const external = /\b(external|exterior|outside)\b/i.test(context);
    const value = fromQuantity ? quantity : opening ? opening.value + (steel ? 0.3 : 0) : external ? 4 : 3;
    const note = fromQuantity ? "Length taken from the current line quantity; confirm against the drawings."
      : opening ? `Assumed from this item's ${opening.label.toLowerCase()} (${opening.value} m)${steel ? " plus 0.3 m total bearing allowance" : ""}; confirm actual length.`
      : `No length retained from the drawings. Provisional ${external ? "external" : "internal/general"} length of ${value} m; change to the actual length before sending.`;
    const ids = new Set(next.dimensions.map(d => d.id));
    let id = "assumed-length";
    while (ids.has(id)) id += "-1";
    next.dimensions.push({ id, label, value: Math.round(value * 10000) / 10000, unit: "m",
      source: "assumed", note });
    if (linearUnit(unit) && next.quantity_op === "direct") {
      next.quantity_op = "length";
      next.quantity_operands = [id];
    }
  }
  return next;
}

/** No recipe means no supported material/installation split, not permission to scale the whole price. */
export function markDimensionPriceReview(before: QuantityDerivation | null | undefined,
  after: QuantityDerivation, unit: string): QuantityDerivation {
  if (!/^(each|item|items|no\.?|nr|nos|pcs|unit|allow)$/i.test(unit.trim())) return after;
  const changed = after.dimensions.some(d => d.unit !== "each"
    && (before?.dimensions.find(old => old.id === d.id)?.value ?? d.originalValue) !== d.value);
  const warning = "Material price review: dimensions changed without a supported material recipe. Price retained; review the supply cost.";
  return changed && !after.note.includes(warning) ? { ...after, note: [warning, after.note].filter(Boolean).join(" ").slice(0, 1000) } : after;
}
